import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';

export default function EmailTemplateManager() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [isNewTemplateOpen, setIsNewTemplateOpen] = useState(false);
  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    variables: [],
    category: 'general'
  });

  // Template kategóriák
  const categories = [
    { id: 'general', name: 'Általános' },
    { id: 'welcome', name: 'Üdvözlő' },
    { id: 'notification', name: 'Értesítés' },
    { id: 'reminder', name: 'Emlékeztető' },
    { id: 'marketing', name: 'Marketing' }
  ];

  // Elérhető változók
  const availableVariables = [
    { key: '{{name}}', description: 'Ügyfél neve' },
    { key: '{{email}}', description: 'Ügyfél email címe' },
    { key: '{{company}}', description: 'Cég neve' },
    { key: '{{date}}', description: 'Dátum' }
  ];

  const defaultTemplates = [
    {
      name: "Üdvözlő Email",
      subject: "Üdvözöljük az Előny Klubban!",
      category: "Rendszer",
      content: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: linear-gradient(to right, #4F46E5, #7C3AED); padding: 40px 20px; text-align: center; color: white; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">Üdvözöljük az Előny Klubban!</h1>
          </div>
          <div style="background: white; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Kedves {name}!
            </p>
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Örömmel üdvözöljük az Előny Klub közösségében! Mostantól Ön is részese lehet számos különleges kedvezménynek és előnynek.
            </p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="https://elonyklub.com/dashboard" style="background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                Irány a vezérlőpult
              </a>
            </div>
            <p style="font-size: 14px; color: #6B7280; margin-top: 30px;">
              Üdvözlettel,<br>
              Az Előny Klub csapata
            </p>
          </div>
        </div>
      `
    },
    {
      name: "Új Kedvezmény Értesítő",
      subject: "Új kedvezmény érhető el!",
      category: "Marketing",
      content: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: linear-gradient(135deg, #059669, #10B981); padding: 40px 20px; text-align: center; color: white; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">Új kedvezmény az Előny Klubban!</h1>
          </div>
          <div style="background: white; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <div style="text-align: center; margin-bottom: 30px;">
              <span style="background: #ECFDF5; color: #059669; padding: 8px 16px; border-radius: 9999px; font-weight: bold;">
                Új kedvezmény
              </span>
            </div>
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Kedves {name}!
            </p>
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Örömmel értesítjük, hogy új kedvezmény érhető el az Előny Klubban! Ne hagyja ki ezt a különleges lehetőséget.
            </p>
            <div style="margin: 30px 0; padding: 20px; background: #F3F4F6; border-radius: 8px;">
              <h2 style="color: #111827; margin-top: 0;">{kedvezmeny_neve}</h2>
              <p style="color: #4B5563;">{kedvezmeny_leirasa}</p>
            </div>
            <div style="text-align: center;">
              <a href="https://elonyklub.com/discounts" style="background-color: #059669; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                Kedvezmény megtekintése
              </a>
            </div>
          </div>
        </div>
      `
    },
    {
      name: "Havi Hírlevél",
      subject: "Havi összefoglaló az Előny Klubtól",
      category: "Hírlevél",
      content: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: linear-gradient(135deg, #2563EB, #3B82F6); padding: 40px 20px; text-align: center; color: white; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">{honap} havi összefoglaló</h1>
          </div>
          <div style="background: white; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Kedves {name}!
            </p>
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Íme a havi összefoglalónk a legújabb kedvezményekről és előnyökről:
            </p>
            <div style="margin: 30px 0;">
              <h2 style="color: #1F2937; font-size: 20px;">Havi statisztika</h2>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-top: 20px;">
                <div style="background: #EFF6FF; padding: 20px; border-radius: 8px; text-align: center;">
                  <div style="font-size: 24px; color: #2563EB; font-weight: bold;">{uj_kedvezmenyek}</div>
                  <div style="color: #4B5563;">Új kedvezmény</div>
                </div>
                <div style="background: #EFF6FF; padding: 20px; border-radius: 8px; text-align: center;">
                  <div style="font-size: 24px; color: #2563EB; font-weight: bold;">{megtakaritas}</div>
                  <div style="color: #4B5563;">Összes megtakarítás</div>
                </div>
              </div>
            </div>
            <div style="text-align: center; margin-top: 30px;">
              <a href="https://elonyklub.com/dashboard" style="background-color: #2563EB; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                Részletek megtekintése
              </a>
            </div>
          </div>
        </div>
      `
    },
    {
      name: "Emlékeztető Email",
      subject: "Ne felejtse el kihasználni kedvezményeit!",
      category: "Emlékeztető",
      content: `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
          <div style="background: linear-gradient(135deg, #9333EA, #C084FC); padding: 40px 20px; text-align: center; color: white; border-radius: 8px 8px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">Kedvezmények várják!</h1>
          </div>
          <div style="background: white; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Kedves {name}!
            </p>
            <p style="font-size: 16px; line-height: 1.6; color: #374151;">
              Észrevettük, hogy még nem használta ki az összes elérhető kedvezményét. Ne hagyja veszni ezeket a lehetőségeket!
            </p>
            <div style="margin: 30px 0; padding: 20px; background: #F5F3FF; border-radius: 8px;">
              <h2 style="color: #111827; margin-top: 0;">Az Ön személyre szabott kedvezményei:</h2>
              <ul style="color: #4B5563; padding-left: 20px;">
                <li style="margin: 10px 0;">{kedvezmeny_1}</li>
                <li style="margin: 10px 0;">{kedvezmeny_2}</li>
                <li style="margin: 10px 0;">{kedvezmeny_3}</li>
              </ul>
            </div>
            <div style="text-align: center;">
              <a href="https://elonyklub.com/discounts" style="background-color: #9333EA; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                Kedvezmények megtekintése
              </a>
            </div>
          </div>
        </div>
      `
    }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'emailTemplates'));
      const templatesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Hiba történt a sablonok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTemplate) {
        // Meglévő sablon frissítése
        await updateDoc(doc(db, 'emailTemplates', selectedTemplate.id), {
          ...formData,
          updatedAt: new Date()
        });
        toast.success('Sablon sikeresen frissítve');
      } else {
        // Új sablon létrehozása
        await addDoc(collection(db, 'emailTemplates'), {
          ...formData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        toast.success('Új sablon sikeresen létrehozva');
      }
      fetchTemplates();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Hiba történt a sablon mentésekor');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await deleteDoc(doc(db, 'emailTemplates', templateId));
      toast.success('Sablon sikeresen törölve');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Hiba történt a sablon törlésekor');
    }
  };

  const handleCloseModal = () => {
    setIsNewTemplateOpen(false);
    setIsEditTemplateOpen(false);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      content: '',
      variables: [],
      category: 'general'
    });
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      content: template.content,
      variables: template.variables || [],
      category: template.category || 'general'
    });
    setIsEditTemplateOpen(true);
  };

  const handleImportDefaults = async () => {
    try {
      for (const template of defaultTemplates) {
        const docRef = await addDoc(collection(db, 'emailTemplates'), {
          ...template,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      toast.success('Alapértelmezett sablonok sikeresen importálva');
      fetchTemplates(); // Frissítjük a listát
    } catch (error) {
      console.error('Error importing default templates:', error);
      toast.error('Hiba történt az alapértelmezett sablonok importálásakor');
    }
  };

  // Előnézet generálása a változók helyettesítésével
  const previewContent = useMemo(() => {
    let preview = formData.content;
    const testData = {
      name: 'Teszt Felhasználó',
      email: 'teszt@example.com',
      company: 'Teszt Cég Kft.',
      date: new Date().toLocaleDateString('hu-HU'),
      kedvezmeny_neve: 'Teszt Kedvezmény',
      kedvezmeny_leirasa: 'Ez egy teszt kedvezmény leírása.',
      honap: new Date().toLocaleString('hu-HU', { month: 'long' }),
      uj_kedvezmenyek: '5',
      megtakaritas: '25 000 Ft',
      kedvezmeny_1: 'Első teszt kedvezmény (20% kedvezmény)',
      kedvezmeny_2: 'Második teszt kedvezmény (5000 Ft visszatérítés)',
      kedvezmeny_3: 'Harmadik teszt kedvezmény (1+1 akció)'
    };

    // Változók helyettesítése
    Object.entries(testData).forEach(([key, value]) => {
      const regex = new RegExp(`{${key}}`, 'g');
      preview = preview.replace(regex, value);
    });

    return preview;
  }, [formData.content]);

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Email Sablonok</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsNewTemplateOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Icons.Plus className="h-5 w-5 mr-2" />
              Új sablon
            </button>
            <button
              onClick={handleImportDefaults}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
            >
              <Icons.Download className="h-5 w-5 mr-2" />
              Alapértelmezett sablonok importálása
            </button>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">{template.subject}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <Icons.Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Icons.Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="prose prose-sm max-w-none text-gray-500">
                <p className="line-clamp-3">{template.content}</p>
              </div>
              <div className="mt-4 flex items-center text-xs text-gray-500">
                <Icons.Tag className="h-4 w-4 mr-1" />
                {categories.find(c => c.id === template.category)?.name || 'Általános'}
              </div>
            </div>
          ))}
        </div>

        {/* Template Modal */}
        <Dialog
          open={isNewTemplateOpen || isEditTemplateOpen}
          onClose={handleCloseModal}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-xl shadow-lg">
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <Dialog.Title className="text-lg font-medium">
                  {isEditTemplateOpen ? 'Sablon szerkesztése' : 'Új sablon létrehozása'}
                </Dialog.Title>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-gray-600 hover:text-gray-700 flex items-center"
                  >
                    <Icons.Eye className="h-5 w-5 mr-1" />
                    {showPreview ? 'Szerkesztés' : 'Előnézet'}
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <Icons.X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex h-[600px]">
                {/* Szerkesztő panel */}
                <div className={`flex-1 ${showPreview ? 'hidden' : 'block'}`}>
                  <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Sablon neve
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tárgy
                        </label>
                        <input
                          type="text"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Kategória
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tartalom
                        </label>
                        <div className="mt-1 relative">
                          <textarea
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            rows={8}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                          <div className="mt-2 text-xs text-gray-500">
                            Elérhető változók:
                            {availableVariables.map(variable => (
                              <span
                                key={variable.key}
                                className="inline-flex items-center px-2 py-1 m-1 rounded-md bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200"
                                onClick={() => {
                                  const textarea = document.querySelector('textarea');
                                  const start = textarea.selectionStart;
                                  const end = textarea.selectionEnd;
                                  const text = textarea.value;
                                  const before = text.substring(0, start);
                                  const after = text.substring(end);
                                  setFormData({
                                    ...formData,
                                    content: before + variable.key + after
                                  });
                                }}
                              >
                                {variable.key}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Előnézet panel */}
                <div className={`flex-1 ${showPreview ? 'block' : 'hidden'} p-6 bg-gray-50 overflow-auto`}>
                  <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <div className="text-sm text-gray-500 mb-2">Tárgy: {formData.subject}</div>
                    <div 
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    <p className="font-medium mb-2">Előnézet információk:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Az előnézet teszt adatokkal van kitöltve</li>
                      <li>A megjelenítés eltérhet a különböző email kliensekben</li>
                      <li>Javasolt tesztelni különböző eszközökön küldés előtt</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Form gombok */}
              <div className="px-6 py-4 border-t bg-gray-50">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    form="templateForm"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                  >
                    {isEditTemplateOpen ? 'Mentés' : 'Létrehozás'}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
} 